import { graphicApi } from '@/api';
import { GridDataProps, GridListView } from '@/components/common';
import { PaginationSetting } from '@/model/CommonModel';
import { GraphicDirectoryInfo, GraphicType } from '@/model/GraphicModel';
import { Pagination, Select, Spin } from 'antd';
import React, { FC, useState } from 'react';
import { batch } from 'react-redux';
import { EditNameModal } from '../EditNameModal/EditNameModal';
import styles from './graphicDirectoryCardView.module.scss';

type Props = {
	handleOnOpenMap: (data: GraphicDirectoryInfo) => {};
	fetchAndSetMapDirectory: (graphicType: GraphicType, paginationSettings: PaginationSetting) => void;
	setPagination: (pagination: PaginationSetting) => void;
	pagination: PaginationSetting;
	mapData: GraphicDirectoryInfo[];
};

const GraphicDirectoryCardView: FC<Props> = ({ pagination, mapData, fetchAndSetMapDirectory, handleOnOpenMap, setPagination }) => {
	//#region state management variable
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showEditNameModal, setShowEditNameModal] = useState<boolean>();
	const [editMap, setEditMap] = useState<GraphicDirectoryInfo>(undefined);
	//#endregion

	const mappedData = mapData.map<GridDataProps<GraphicDirectoryInfo>>(item => ({
		id: item.Id,
		title: item.Name,
		subTitle: item.LastUpdated.toString(),
		extraData: {
			...item,
			onClick: () => {
				handleOnOpenMap(item);
			},
		},
	}));

	const handleOnDelete = async (id: number) => {
		const graphicItem = mappedData.find(item => id === item.id);
		setIsLoading(true);
		await graphicApi.deleteMap(graphicItem.id, graphicItem.extraData.Type);
	};

	const handleOnEdit = (id: number) => {
		const map = mapData.find(d => d.Id === id);
		batch(() => {
			setEditMap(map);
			setShowEditNameModal(true);
		});
	};

	const onPaginationChanged = (pageNumber: number, pageSize: number) => {
		const newPagination: PaginationSetting = {
			...pagination,
			PageNumber: pageNumber,
			PageSize: pageSize,
		};
		setPagination(newPagination);
		fetchAndSetMapDirectory(GraphicType.Web, newPagination);
	};

	const onEditCancel = () => {
		setShowEditNameModal(false);
	};

	const onEditConfirm = () => {
		setShowEditNameModal(false);
		fetchAndSetMapDirectory(GraphicType.Web, pagination);
	};

	return (
		<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
			<GridListView
				canDelete={true}
				canEdit={true}
				data={mappedData}
				isUserAdmin={true}
				actions={{
					onDelete: async (id: number) => {
						await handleOnDelete(id);
						fetchAndSetMapDirectory(GraphicType.Web, pagination);
						setIsLoading(false);
						return false;
					},
					onEdit: handleOnEdit,
				}}
			/>
			{mappedData?.length > 0 && (
				<div className={styles.paginationContainer}>
					<Pagination
						size='small'
						showSizeChanger={false}
						pageSizeOptions={['12']}
						current={pagination.PageNumber}
						pageSize={pagination.PageSize}
						total={pagination.TotalItems}
						onChange={onPaginationChanged}
						showTotal={value => `${value.toLocaleString('en-US')} ${_('Items').toLowerCase()}`}
					/>
					<Select value={'12 / page'} disabled />
				</div>
			)}
			{showEditNameModal && editMap && <EditNameModal maxLength={50} map={editMap} onCancel={onEditCancel} onConfirm={onEditConfirm} />}
		</Spin>
	);
};

export { GraphicDirectoryCardView };
